import React, { useMemo, useState, useEffect, useCallback } from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { selectReportCurrency } from "../../../../slices/report/selectors";
import useReportFormat from "../useReportFormat";

const GROUP_COST_TYPE = "cost_type";
const GROUP_CATEGORY = "category";

const GROUP_OPTIONS = [
  { id: GROUP_COST_TYPE, label: "Cost Type", field: "by_cost_type" },
  { id: GROUP_CATEGORY, label: "Category", field: "by_category" },
];

const PALETTE = [
  "#7c3aed",
  "#0891b2",
  "#059669",
  "#d97706",
  "#dc2626",
  "#db2777",
  "#2563eb",
  "#ca8a04",
];

const formatMonthLabel = (ym) => {
  const d = new Date(ym + "-01");
  return d.toLocaleString("en-US", { month: "short" }) + " " + d.getFullYear();
};

const yFormatter = (val) => {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `${Math.round(val / 1_000)}K`;
  return String(Math.round(val));
};

const pickerSlotProps = {
  textField: {
    size: "small",
    sx: {
      width: 150,
      "& .MuiInputBase-root": { fontSize: "0.78rem", fontWeight: 500 },
      "& .MuiInputBase-input": { padding: "5px 8px" },
    },
  },
};

const CostTypeStackedChart = ({ months = [], loading = false }) => {
  const formatCurrency = useReportFormat();
  const currency = useSelector(selectReportCurrency);
  const [groupBy, setGroupBy] = useState(GROUP_COST_TYPE);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const lastMonth = months[months.length - 1]?.month ?? null;
  const availableMonths = useMemo(() => new Set(months.map((m) => m.month)), [months]);
  const shouldDisableMonth = useCallback((date) => !availableMonths.has(date.format("YYYY-MM")), [availableMonths]);

  const sortedAvailable = useMemo(() => Array.from(availableMonths).sort(), [availableMonths]);
  const defaultEndYM = lastMonth ?? dayjs().startOf("month").format("YYYY-MM");
  const targetStartYM = dayjs(defaultEndYM + "-01").subtract(6, "month").format("YYYY-MM");
  const defaultStartYM = sortedAvailable.find((m) => m >= targetStartYM) ?? sortedAvailable[0] ?? targetStartYM;

  const resetRange = useCallback(() => {
    setEndDate(dayjs(defaultEndYM + "-01"));
    setStartDate(dayjs(defaultStartYM + "-01"));
  }, [defaultEndYM, defaultStartYM]);

  useEffect(() => {
    resetRange();
  }, [resetRange]);

  const activeOption = GROUP_OPTIONS.find((o) => o.id === groupBy) || GROUP_OPTIONS[0];

  const startYM = startDate?.format("YYYY-MM") ?? "";
  const endYM = endDate?.format("YYYY-MM") ?? "";
  const isFiltered = startYM !== defaultStartYM || endYM !== defaultEndYM;

  const visibleMonths = useMemo(() => {
    return months.filter((m) => {
      if (startYM && m.month < startYM) return false;
      if (endYM && m.month > endYM) return false;
      return true;
    });
  }, [months, startYM, endYM]);

  const { categories, series, hasData } = useMemo(() => {
    const totalsById = new Map();
    visibleMonths.forEach((m) => {
      (m[activeOption.field] || []).forEach((g) => {
        const current = totalsById.get(g.id) || { id: g.id, name: g.name || "Uncategorized", total: 0 };
        current.total += g.actual ?? 0;
        totalsById.set(g.id, current);
      });
    });

    const sortedGroups = Array.from(totalsById.values()).sort((a, b) => b.total - a.total);
    const cats = visibleMonths.map((m) => formatMonthLabel(m.month));

    const built = sortedGroups
      .map((g, i) => ({
        label: g.name,
        data: visibleMonths.map((m) => {
          const row = (m[activeOption.field] || []).find((x) => x.id === g.id);
          return row?.actual ?? 0;
        }),
        stack: "total",
        color: PALETTE[i % PALETTE.length],
        valueFormatter: (val) => (val === 0 ? null : formatCurrency(val)),
      }))
      .filter((s) => s.data.some((v) => v > 0));

    return {
      categories: cats,
      series: built,
      hasData: built.some((s) => s.data.some((v) => v > 0)),
    };
  }, [visibleMonths, activeOption.field, formatCurrency]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <div
        style={{
          background: "white",
          borderRadius: 16,
          padding: 32,
          border: "1px solid #f0f4ff",
          boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
          marginBottom: 32,
        }}
      >
        {/* Header row */}
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <p style={{ fontWeight: 700, fontSize: "1rem", color: "#002045", margin: 0, marginBottom: 2 }}>
              Monthly Spend by {activeOption.label}
            </p>
            <p style={{ fontSize: "0.78rem", color: "#64748b", margin: 0, fontWeight: 500 }}>
              Actual spend per {activeOption.label.toLowerCase()}
              {currency && (
                <span style={{ marginLeft: 6, color: "#94a3b8" }}>· {currency}</span>
              )}
            </p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
            {/* Date range pickers */}
            <DatePicker
              label="From"
              views={["year", "month"]}
              openTo="month"
              value={startDate}
              maxDate={endDate ?? undefined}
              onChange={setStartDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            <span style={{ fontSize: "0.78rem", color: "#94a3b8" }}>–</span>
            <DatePicker
              label="To"
              views={["year", "month"]}
              openTo="month"
              value={endDate}
              minDate={startDate ?? undefined}
              onChange={setEndDate}
              shouldDisableMonth={shouldDisableMonth}
              slotProps={pickerSlotProps}
            />
            {isFiltered && (
              <button
                type="button"
                onClick={resetRange}
                style={{
                  border: "none",
                  background: "none",
                  color: "#7c3aed",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  padding: "4px 2px",
                  textDecoration: "underline",
                  textUnderlineOffset: 2,
                }}
              >
                Reset
              </button>
            )}

            {/* Group-by toggle */}
            <div
              style={{
                display: "inline-flex",
                background: "#f1f5f9",
                padding: 3,
                borderRadius: 8,
                gap: 3,
              }}
            >
              {GROUP_OPTIONS.map((opt) => {
                const active = groupBy === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setGroupBy(opt.id)}
                    style={{
                      border: "none",
                      background: active ? "#ffffff" : "transparent",
                      color: active ? "#002045" : "#64748b",
                      fontWeight: 600,
                      fontSize: "0.78rem",
                      padding: "5px 12px",
                      borderRadius: 6,
                      cursor: "pointer",
                      boxShadow: active ? "0 1px 2px rgba(0,0,0,0.06)" : "none",
                      transition: "background 0.15s, color 0.15s",
                    }}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Chart body */}
        {!loading && !hasData ? (
          <div
            style={{
              minHeight: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#94a3b8",
              fontSize: "0.85rem",
              fontWeight: 600,
            }}
          >
            No spending data yet
          </div>
        ) : (
          <BarChart
            key={groupBy}
            loading={loading}
            series={loading ? [] : series}
            xAxis={[{ data: loading ? [] : categories, scaleType: "band" }]}
            yAxis={[{ valueFormatter: yFormatter }]}
            height={380}
            margin={{ top: 10, right: 20, bottom: 70, left: 60 }}
            slotProps={{
              legend: {
                direction: "row",
                position: { vertical: "bottom", horizontal: "middle" },
                padding: 0,
                itemMarkWidth: 10,
                itemMarkHeight: 10,
                markGap: 6,
                itemGap: 16,
                labelStyle: { fontSize: 12, fontWeight: 500, fill: "#475569" },
              },
            }}
          />
        )}
      </div>
    </LocalizationProvider>
  );
};

export default CostTypeStackedChart;
